import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { formatDate } from '@/components/member-profile/labels.ts';
import LicenseQRCodeCard from '@/components/qrcode/LicenseQRCodeCard.tsx';
import { ROUTES } from '@/config/routes';
import { useHydration } from '@/hooks/useHydration';
import { getFileUrl } from '@/lib/file-url';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import { type AuthUser, computeAgeCategory } from '@shared';
import { ClubPlan, Gender, Role, UserPlan, getInitials } from '@shared';
import {
  ArrowRight,
  Check,
  ChevronRight,
  CircleDot,
  Copy,
  CreditCard,
  Gift,
  Loader2,
  LogOut,
  Mail,
  Shield,
  Sparkles,
  User,
  UserCheck,
} from 'lucide-react';

import { AdminPlatformStats, AdminQuickActions } from './mon-espace/admin-section';
import { ClubAdminClubCard, ClubAdminQuickActions, ClubAdminStats } from './mon-espace/club-admin-section';
import { MemberQuickActions, MemberRecentRegistrations, MemberStats } from './mon-espace/member-section';
import {
  OrganizerOrganizations,
  OrganizerQuickActions,
  OrganizerRecentCompetitions,
} from './mon-espace/organizer-section';

const ADMIN_URL = (import.meta.env.VITE_URL_ADMIN as string | undefined) ?? 'http://localhost:5231';

// ─── Helpers ────────────────────────────────────────────────────

function getJwtExpMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

function useTokenCountdown(expMs: number | null): number {
  const [remaining, setRemaining] = useState(() => (expMs ? Math.max(0, Math.floor((expMs - Date.now()) / 1000)) : 0));
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  useEffect(() => {
    if (!expMs) return;
    const tick = () => setRemaining(Math.max(0, Math.floor((expMs - Date.now()) / 1000)));
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [expMs]);

  return remaining;
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ORGANIZATION_ADMIN: 'Organisateur',
  CLUB_ADMIN: 'Admin Club',
  MEMBER: 'Joueur',
};

const ROLE_COLORS: Record<string, string> = {
  SUPER_ADMIN: 'bg-violet-500/20 text-violet-100',
  ORGANIZATION_ADMIN: 'bg-indigo-500/20 text-indigo-100',
  CLUB_ADMIN: 'bg-emerald-500/20 text-emerald-100',
  MEMBER: 'bg-blue-500/20 text-blue-100',
};

const HERO_GRADIENTS: Record<string, string> = {
  SUPER_ADMIN: 'from-violet-600 via-violet-700 to-purple-800',
  ORGANIZATION_ADMIN: 'from-indigo-600 via-indigo-700 to-purple-700',
  CLUB_ADMIN: 'from-emerald-600 via-emerald-700 to-teal-700',
  MEMBER: 'from-blue-600 via-blue-700 to-indigo-700',
};

function getProfileUrl(role: string): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return `${ADMIN_URL}/admin/settings`;
    case 'ORGANIZATION_ADMIN':
      return `${ADMIN_URL}/organizer/settings`;
    case 'CLUB_ADMIN':
      return `${ADMIN_URL}/club/settings`;
    default:
      return `${ADMIN_URL}/player/profile`;
  }
}

function getReferralRoute(role: string): string {
  switch (role) {
    case 'ORGANIZATION_ADMIN':
      return ROUTES.PARRAINAGE_ORGANISATEUR;
    case 'CLUB_ADMIN':
    default:
      return ROUTES.PARRAINAGE;
  }
}

// ─── Loading ────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div className='flex min-h-screen items-center justify-center'>
      <div className='flex flex-col items-center gap-4'>
        <div className='h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600' />
        <p className='text-sm text-slate-500'>Chargement de votre espace…</p>
      </div>
    </div>
  );
}

// ─── Role-specific content ──────────────────────────────────────

function RoleQuickActions({ user }: { user: AuthUser }) {
  switch (user.role) {
    case Role.SUPER_ADMIN:
      return <AdminQuickActions />;
    case Role.ORGANIZATION_ADMIN:
      return <OrganizerQuickActions user={user} />;
    case Role.CLUB_ADMIN:
      return <ClubAdminQuickActions user={user} />;
    default:
      return <MemberQuickActions />;
  }
}

function RoleMainContent({ user }: { user: AuthUser }) {
  switch (user.role) {
    case Role.SUPER_ADMIN:
      return <AdminPlatformStats />;
    case Role.ORGANIZATION_ADMIN:
      return (
        <>
          <OrganizerOrganizations user={user} />
          <OrganizerRecentCompetitions user={user} />
        </>
      );
    case Role.CLUB_ADMIN:
      return <ClubAdminStats user={user} />;
    default:
      return (
        <>
          <MemberStats />
          <MemberRecentRegistrations />
        </>
      );
  }
}

function RoleSideContent({ user }: { user: AuthUser }) {
  switch (user.role) {
    case Role.SUPER_ADMIN:
      return null;
    case Role.ORGANIZATION_ADMIN:
      return null;
    case Role.CLUB_ADMIN:
      return <ClubAdminClubCard user={user} />;
    default:
      return <MemberClubCard user={user} />;
  }
}

// ─── Shared side components ─────────────────────────────────────

function MemberClubCard({ user }: { user: AuthUser }) {
  if (user.club) {
    return (
      <div className='rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
        <h2 className='mb-4 flex items-center gap-2 text-base font-bold text-slate-900'>
          <Shield className='h-4 w-4 text-emerald-500' />
          Mon club
        </h2>
        <Link
          to={ROUTES.CLUB_PROFILE.replace(':id', String(user.club.id))}
          className='group flex items-center gap-3 rounded-xl bg-slate-50 p-3 transition hover:bg-slate-100'
        >
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-emerald-500 to-teal-600 text-xs font-bold text-white'>
            {getInitials(user.club.name)}
          </div>
          <div className='flex-1 min-w-0'>
            <p className='truncate text-sm font-bold text-slate-900'>{user.club.name}</p>
            {user.club.city && <p className='text-xs text-slate-500'>{user.club.city}</p>}
          </div>
          <ChevronRight className='h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-emerald-500' />
        </Link>
      </div>
    );
  }

  return (
    <div className='rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
      <h2 className='mb-3 flex items-center gap-2 text-base font-bold text-slate-900'>
        <Shield className='h-4 w-4 text-slate-400' />
        Club
      </h2>
      <p className='text-sm text-slate-500'>Vous n&apos;êtes pas encore rattaché à un club.</p>
      <Link
        to={ROUTES.CLUBS}
        className='mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700'
      >
        Découvrir les clubs <ChevronRight className='h-4 w-4' />
      </Link>
    </div>
  );
}

// ─── Subscription card ─────────────────────────────────────────

function SubscriptionCard({ user }: { user: AuthUser }) {
  const isClub = user.role === Role.CLUB_ADMIN;
  const plan = isClub ? user.club?.clubPlan : user.userPlan;
  const isPremium = isClub ? plan === ClubPlan.PRO : plan === UserPlan.PLUS;

  const planLabel = isPremium ? (isClub ? 'Club Pro' : 'Plus') : 'Gratuit';

  const prefsUrl = isClub
    ? `${ADMIN_URL}/club/settings?tab=subscription`
    : `${ADMIN_URL}/player/preferences?tab=subscription`;

  return (
    <div className='rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
      <h2 className='mb-4 flex items-center gap-2 text-base font-bold text-slate-900'>
        <CreditCard className='h-4 w-4 text-blue-500' />
        Mon abonnement
      </h2>
      <div className={`flex items-center gap-3 rounded-xl p-3 ${isPremium ? 'bg-amber-50' : 'bg-slate-50'}`}>
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            isPremium ? 'bg-linear-to-br from-amber-400 to-amber-600 text-white' : 'bg-slate-200 text-slate-500'
          }`}
        >
          {isPremium ? <Sparkles className='h-5 w-5' /> : <CreditCard className='h-5 w-5' />}
        </div>
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-bold text-slate-900'>{planLabel}</p>
          <p className='text-xs text-slate-500'>{isPremium ? 'Abonnement actif' : 'Aucun abonnement'}</p>
        </div>
      </div>
      <a
        href={prefsUrl}
        className='mt-4 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700'
      >
        {isPremium ? 'Gérer mon abonnement' : 'Découvrir les offres'} <ArrowRight className='h-3 w-3' />
      </a>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────

export default function MonEspace() {
  const hydrated = useHydration();
  const sessionChecked = useAuthStore(s => s.sessionChecked);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const isImpersonating = useAuthStore(s => s.isImpersonating);
  const stopImpersonation = useAuthStore(s => s.stopImpersonation);
  const accessToken = useAuthStore(s => s.accessToken);
  const navigate = useNavigate();
  const referralEnabled = useSaasStore(s => s.referralEnabled);
  const playerPlusEnabled = useSaasStore(s => s.playerPlusEnabled);
  const [copied, setCopied] = useState(false);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  const tokenExpMs = isImpersonating && accessToken ? getJwtExpMs(accessToken) : null;
  const countdown = useTokenCountdown(tokenExpMs);

  async function handleStopImpersonation() {
    setStoppingImpersonation(true);
    await stopImpersonation();
  }

  function copyReferralCode() {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  useEffect(() => {
    if (!isImpersonating || !tokenExpMs || countdown > 0) return;
    void stopImpersonation();
  }, [countdown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hydrated && sessionChecked && !isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?redirectTo=${encodeURIComponent(ROUTES.ROLE_LANDING)}`, { replace: true });
    }
  }, [hydrated, sessionChecked, isAuthenticated, navigate]);

  if (!hydrated || !sessionChecked || !user) {
    return <LoadingScreen />;
  }

  const initials = getInitials(`${user.firstName ?? ''} ${user.lastName ?? ''}`);
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || user.email;
  const heroGradient = HERO_GRADIENTS[user.role] ?? HERO_GRADIENTS.MEMBER;
  const roleLabel = ROLE_LABELS[user.role] ?? 'Joueur';
  const roleColor = ROLE_COLORS[user.role] ?? ROLE_COLORS.MEMBER;
  const profileUrl = getProfileUrl(user.role);
  const referralRoute = getReferralRoute(user.role);
  const sideContent = RoleSideContent({ user });
  const showSidePanel = sideContent !== null || user.role !== Role.SUPER_ADMIN;

  return (
    <div className='min-h-screen bg-slate-50'>
      <PageMeta title='Mon espace' robots='noindex, nofollow' />

      {/* Hero */}
      <div className={`bg-linear-to-r ${heroGradient} pb-32 pt-24`}>
        <div className='mx-auto max-w-5xl px-6'>
          <div className='flex flex-col items-start gap-6 sm:flex-row sm:items-center'>
            {user.files?.[0] ? (
              <img
                src={getFileUrl(user.files[0])}
                alt={fullName}
                className='h-20 w-20 shrink-0 rounded-2xl object-cover shadow-lg'
              />
            ) : (
              <div className='flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-2xl font-extrabold text-white shadow-lg backdrop-blur'>
                {initials}
              </div>
            )}
            <div className='flex-1'>
              <div className='flex items-center gap-2'>
                <p className='text-sm font-semibold text-blue-200'>Mon espace</p>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${roleColor}`}>{roleLabel}</span>
              </div>
              <h1 className='text-3xl font-extrabold text-white'>Bonjour, {user.firstName ?? fullName}</h1>
              <div className='mt-2 flex flex-wrap gap-2'>
                <span className='inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white'>
                  <CircleDot className='h-3 w-3' />
                  {user.email}
                </span>
                {user.club && (
                  <Link to={`/clubs/${user.clubId}`}>
                    <span className='inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white'>
                      <Shield className='h-3 w-3' />
                      {user.club.name}
                    </span>
                  </Link>
                )}
                {user.organizationsAdmin?.map(m => (
                  <Link key={m.organizationId} to={`/organisateurs/${m.organizationId}`}>
                    <span className='inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white'>
                      <Shield className='h-3 w-3' />
                      {m.organization.name}
                    </span>
                  </Link>
                ))}
              </div>
              <div className='mt-2 flex flex-wrap gap-2'>
                {user.emailVerified ? (
                  <span className='inline-flex items-center gap-1.5 rounded-full bg-white/45 px-3 py-1 text-xs font-semibold text-white'>
                    <Mail className='h-3 w-3 text-green-600' />
                    Email vérifié
                  </span>
                ) : (
                  <span className='inline-flex items-center gap-1.5 rounded-full bg-white/45 px-3 py-1 text-xs font-semibold text-white'>
                    <Mail className='h-3 w-3 text-red-600' />
                    Email non vérifié
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='mx-auto -mt-20 max-w-5xl px-6 pb-20'>
        {/* Quick actions (role-specific) */}
        <RoleQuickActions user={user} />

        {/* Main content (role-specific) */}
        <RoleMainContent user={user} />

        {/* Profile + Side panel */}
        <div className={`grid grid-cols-1 gap-6 ${showSidePanel ? 'lg:grid-cols-3' : ''}`}>
          <div
            className={`${showSidePanel ? 'lg:col-span-2' : ''} rounded-2xl bg-white border border-slate-100 shadow-sm p-6`}
          >
            <h2 className='mb-5 flex items-center gap-2 text-base font-bold text-slate-900'>
              <User className='h-4 w-4 text-blue-500' />
              Mon profil
            </h2>
            <dl className='space-y-4'>
              <div className='flex items-center justify-between border-b border-slate-50 pb-4'>
                <dt className='text-sm text-slate-500'>Nom complet</dt>
                <dd className='text-sm font-semibold text-slate-900'>{fullName}</dd>
              </div>
              <div className='flex items-center justify-between border-b border-slate-50 pb-4'>
                <dt className='text-sm text-slate-500'>Email</dt>
                <dd className='text-sm font-semibold text-slate-900'>{user.email}</dd>
              </div>
              {user.mobile && (
                <div className='flex items-center justify-between border-b border-slate-50 pb-4'>
                  <dt className='text-sm text-slate-500'>Téléphone</dt>
                  <dd className='text-sm font-semibold text-slate-900'>{user.mobile}</dd>
                </div>
              )}
              {user.licenseNumber && (
                <div className='flex items-center justify-between border-b border-slate-50 pb-4'>
                  <dt className='text-sm text-slate-500'>Licence FFPJP</dt>
                  <dd className='font-mono text-sm font-semibold text-slate-900'>{user.licenseNumber}</dd>
                </div>
              )}

              {user.birthDate && (
                <div className='flex items-center justify-between border-b border-slate-50 pb-4'>
                  <dt className='text-sm text-slate-500'>Catégorie</dt>
                  <dd className='font-mono text-sm font-semibold text-slate-900'>
                    {computeAgeCategory(new Date(user.birthDate))}
                  </dd>
                </div>
              )}

              {user.birthDate && (
                <div className='flex items-center justify-between border-b border-slate-50 pb-4'>
                  <dt className='text-sm text-slate-500'>Date de naissance</dt>
                  <dd className='font-mono text-sm font-semibold text-slate-900'>{formatDate(user.birthDate)}</dd>
                </div>
              )}
              {user.gender && (
                <div className='flex items-center justify-between border-b border-slate-50 pb-4'>
                  <dt className='text-sm text-slate-500'>Sexe</dt>
                  <dd className='font-mono text-sm font-semibold text-slate-900'>
                    {user.gender === Gender.MALE ? '♂️ Homme' : '♀️ Femme'}
                  </dd>
                </div>
              )}
              {user.club && (
                <div className='flex items-center justify-between pb-2'>
                  <dt className='text-sm text-slate-500'>Club</dt>
                  <dd className='text-sm font-semibold text-slate-900'>{user.club.name}</dd>
                </div>
              )}
            </dl>
            <div className='w-full flex flex-row justify-between mt-6 border-t border-slate-100 pt-5'>
              <a
                href={profileUrl}
                className='flex flex-row items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700'
              >
                Modifier mon profil
                <ChevronRight className='h-4 w-4' />
              </a>
              <Link
                to={`/profil/${user.id}`}
                className='flex flex-row items-end gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700'
              >
                Voir mon profil public
                <ChevronRight className='h-4 w-4' />
              </Link>
            </div>
          </div>

          {/* Side panel */}
          {showSidePanel && (
            <div className='space-y-6'>
              {/* Profile card */}
              {user.licenseNumber && <LicenseQRCodeCard licenseNumber={user.licenseNumber} />}

              {sideContent}

              {/* Parrainage (masqué si désactivé dans SaasParams) */}
              {referralEnabled && (
                <div className='rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
                  <h2 className='mb-4 flex items-center gap-2 text-base font-bold text-slate-900'>
                    <Gift className='h-4 w-4 text-indigo-500' />
                    Mon code parrainage
                  </h2>
                  <p className='mb-3 text-xs text-slate-500 leading-relaxed'>
                    Partagez ce code et gagnez des récompenses pour chaque filleul actif.
                  </p>
                  <div className='flex gap-2'>
                    <input
                      readOnly
                      value={user.referralCode ?? '—'}
                      className='flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm font-bold tracking-widest text-slate-900 select-all cursor-text'
                    />
                    <button
                      onClick={copyReferralCode}
                      disabled={!user.referralCode}
                      title='Copier le code'
                      className='cursor-pointer flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40'
                    >
                      {copied ? <Check className='h-4 w-4 text-emerald-500' /> : <Copy className='h-4 w-4' />}
                    </button>
                  </div>
                  {copied && <p className='mt-2 text-xs font-semibold text-emerald-600'>Code copié !</p>}
                  <Link
                    to={referralRoute}
                    className='mt-4 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700'
                  >
                    Voir le programme <ArrowRight className='h-3 w-3' />
                  </Link>
                </div>
              )}

              {/* Abonnement (masqué pour les joueurs si playerPlusEnabled=false) */}
              {(user.role === Role.CLUB_ADMIN || (user.role === Role.MEMBER && playerPlusEnabled)) && (
                <SubscriptionCard user={user} />
              )}

              {/* Impersonate exit */}
              {isImpersonating && (
                <div className='rounded-2xl border border-amber-200 bg-amber-50 shadow-sm p-6'>
                  <h2 className='mb-3 flex items-center gap-2 text-sm font-bold text-amber-800'>
                    <UserCheck className='h-4 w-4' />
                    Mode test actif
                  </h2>
                  <p className='mb-4 text-xs text-amber-700 leading-relaxed'>
                    Vous naviguez en tant que cet utilisateur. La session expire dans :
                  </p>
                  <div
                    className={`mb-4 text-center font-mono text-2xl font-extrabold tabular-nums ${
                      countdown <= 60 ? 'text-red-600 animate-pulse' : 'text-amber-700'
                    }`}
                  >
                    {formatCountdown(countdown)}
                  </div>
                  <button
                    onClick={() => void handleStopImpersonation()}
                    disabled={stoppingImpersonation}
                    className='flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:opacity-60 cursor-pointer'
                  >
                    {stoppingImpersonation ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      <LogOut className='h-4 w-4' />
                    )}
                    Quitter l&apos;impersonation
                  </button>
                </div>
              )}

              {/* Logout */}
              <div className='rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
                <button
                  onClick={() => logout()}
                  className='w-full rounded-xl border border-red-200 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50 cursor-pointer'
                >
                  Se déconnecter
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
